/**
 * 편집 입력을 ChangeKey 단위의 단일 pending change로 정규화한다.
 *
 * 동일 XBRL fact가 표마다 다른 cellId로 렌더될 수 있으므로 identity를 키로 삼는다.
 * identity는 셀이 자기 dlView의 values에서 직접 해석해 넘겨준다 — cellId로 identity를
 * 역조회하면 안 된다. cellId에는 축이 없어서 축만 다른 두 표의 셀이 같은 cellId를 갖고,
 * 전역 역조회는 그중 하나를 임의로 골라 엉뚱한 fact에 값을 쓰게 된다.
 */

import type { FsType } from "@/apis/ixd-editor/types";
import { makeChangeKey, type ChangeKey } from "@/utils/ixd-cell-model";
import type {
  CellChangeEntry,
  CellChangeReference,
  FsTypeCellChanges,
} from "./edit-session-store";

/** 셀이 올려보내는 편집 입력. identityKey는 셀의 dlView 스코프에서 해석된 값이다. */
export interface CellEditInput {
  fsType: FsType;
  cellId: string;
  identityKey?: string;
  /** 편집이 발생한 실제 보고서 셀 위치 UID */
  locationUid?: string;
  newValue: string;
}

interface BuildCellChangePlanInput {
  entries: CellEditInput[];
  allCellChanges: FsTypeCellChanges;
}

export interface ConflictingCellChangeGroup {
  fsType: FsType;
  changeKey: ChangeKey;
  cellIds: string[];
  values: string[];
  /** 이 그룹을 만든 입력들의 entries 인덱스 */
  entryIndexes: number[];
}

export interface CellChangePlan {
  /** ChangeKey별 마지막 입력 하나만 남긴 대표 변경 (충돌 그룹은 제외) */
  entries: CellChangeEntry[];
  /** 새 대표 변경을 적용하기 전에 제거할 같은 ChangeKey의 기존 변경 */
  cellsToRemove: CellChangeReference[];
  /** 한 batch 안에서 동일 ChangeKey에 서로 다른 값을 입력한 그룹 */
  conflicts: ConflictingCellChangeGroup[];
}

interface PlannedGroup {
  changeKey: ChangeKey;
  latestEntry: CellChangeEntry;
  cellIds: Set<string>;
  values: Set<string>;
  locationUids: Set<string>;
  entryIndexes: number[];
}

const FS_TYPES: FsType[] = ["consolidated", "separate"];

export const buildIdentityAwareCellChangePlan = ({
  entries,
  allCellChanges,
}: BuildCellChangePlanInput): CellChangePlan => {
  const groupsByFsType: Record<FsType, Map<ChangeKey, PlannedGroup>> = {
    consolidated: new Map(),
    separate: new Map(),
  };

  for (const [index, input] of entries.entries()) {
    const changeKey = makeChangeKey(input.identityKey, input.cellId);
    const entry: CellChangeEntry = {
      fsType: input.fsType,
      changeKey,
      newValue: input.newValue,
    };
    const existing = groupsByFsType[input.fsType].get(changeKey);
    if (existing) {
      existing.latestEntry = entry;
      existing.cellIds.add(input.cellId);
      existing.values.add(input.newValue);
      existing.entryIndexes.push(index);
      if (input.locationUid) existing.locationUids.add(input.locationUid);
      continue;
    }
    groupsByFsType[input.fsType].set(changeKey, {
      changeKey,
      latestEntry: entry,
      cellIds: new Set([input.cellId]),
      values: new Set([input.newValue]),
      locationUids: new Set(input.locationUid ? [input.locationUid] : []),
      entryIndexes: [index],
    });
  }

  const plannedEntries: CellChangeEntry[] = [];
  const cellsToRemove: CellChangeReference[] = [];
  const conflicts: ConflictingCellChangeGroup[] = [];

  for (const fsType of FS_TYPES) {
    const groups = groupsByFsType[fsType];
    for (const group of groups.values()) {
      // 충돌 그룹은 통째로 제외한다. 대표값 하나를 골라 적용하면 사용자가 넣지 않은
      // 값이 조용히 연동 셀 전체에 퍼진다 — 나머지 셀은 정상 반영된다.
      if (group.values.size > 1) {
        conflicts.push({
          fsType,
          changeKey: group.changeKey,
          cellIds: Array.from(group.cellIds),
          values: Array.from(group.values),
          entryIndexes: group.entryIndexes,
        });
        continue;
      }
      plannedEntries.push(
        group.locationUids.size > 0
          ? {
              ...group.latestEntry,
              locationUids: Array.from(group.locationUids),
            }
          : group.latestEntry,
      );
    }

    for (const changeKey of Object.keys(allCellChanges[fsType])) {
      // 적용하지 않는 충돌 그룹의 기존 변경은 그대로 둔다.
      if (groups.get(changeKey)?.values.size === 1) {
        cellsToRemove.push({ fsType, changeKey });
      }
    }
  }

  return { entries: plannedEntries, cellsToRemove, conflicts };
};
